import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Stack, Text } from 'braid-design-system';
import { useFeatureFlags } from '../../../shared/providers/featureFlagProvider';
import type { SearchCompanyProfileResult } from '../../../shared/services/companyProfileService/types';
import {
  CURRENT_PAGE_OVERALL,
  trackContinuePressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import type { CompanyNameValue } from '../../questions/AutoSuggestionQuestions/CompanyNameQuestion/CompanyNameQuestion';
import type { CompanyNameValueV2 } from '../../questions/AutoSuggestionQuestions/CompanyNameQuestion/CompanyNameQuestionV2';
import { OverallRatingQuestion } from '../../questions/RatingQuesions/OverallRatingQuestion';
import { OverallExperienceDetailsQuestion } from '../../questions/SimpleQuestions/OverallExperienceDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';
import { CompanyNameField } from '../OverallRatingPage';

type Props = PageProps & {
  isSubmitting: boolean;
  searchCompanyProfiles: (query: string) => Promise<SearchCompanyProfileResult>;
  hideCompanyNameSearch?: boolean;
};

export const ReviewCompanyPage = ({
  review,
  reviewFormContext,
  searchCompanyProfiles,
  hideCompanyNameSearch,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();
  const { cookieFeatureFlags } = useFeatureFlags();

  const fields = {
    overallRating: review?.overallRating,
    overallDescription: review?.overallExperienceDetails,
  };

  const { overallRating, overallDescription } = validationRules(
    config.language,
  );

  const rules = {
    overallRating,
    overallDescription,
  } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    handleUpdateMultipleValuesWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOverallRatingChange = (value: Rating) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'overallRating',
      previousValue: review,
    })(value);
  };

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_OVERALL,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });

    if (!isValid) {
      return;
    }

    setPage(ExperimentTwoProgress.WorkLifeBalance);
  };

  const onSearchCompanyProfiles = async (query: string) => {
    return searchCompanyProfiles(query);
  };

  const handleCompanyNameFieldChange = (value: CompanyNameValueV2) => {
    handleUpdateMultipleValuesWithValidation({
      setter,
      previousValue: review,
    })({
      companyId: value.companyId,
      organisationId: value.organisationId,
      companyName: value.companyName,
      companyProfilePublished: value.published,
      companyNameFieldMode: value.mode,
    });
  };

  const handleCompanyNameChange = (value: CompanyNameValue) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'companyName',
      previousValue: review,
    })(value.companyName);

    setter((prev) => ({
      ...prev,
      companyId: value.companyId,
      organisationId: value.organisationId,
      companyProfilePublished: value.published,
    }));
  };

  const getCompanyNameFieldErrorMessage = () => {
    const companyNameMessage = getMessage('companyName');
    return companyNameMessage.length > 0
      ? companyNameMessage
      : getMessage('companyId');
  };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      heading={
        review?.companyName
          ? `Review working at ${review?.companyName}`
          : 'Write a company review'
      }
      hideStepper={true}
      onContinue={handleOnContinue}
      isContinueLoading={isSubmitting}
    >
      <Stack space="large">
        <Text tone="secondary">
          Your anonymous review may be shown on our website.
        </Text>
        <CompanyNameField
          handleCompanyNameChange={handleCompanyNameChange}
          handleCompanyNameFieldChange={handleCompanyNameFieldChange}
          onSearchCompanyProfiles={onSearchCompanyProfiles}
          review={review}
          hideCompanyNameSearch={hideCompanyNameSearch}
          config={config}
          reviewFormContext={reviewFormContext}
          tone={getTone('companyName')}
          message={getMessage('companyName')}
          showV2={cookieFeatureFlags.COMPANY_NAME_FIELD_V2}
          errorMessage={getCompanyNameFieldErrorMessage()}
        />
        <OverallRatingQuestion
          onChange={handleOverallRatingChange}
          value={review?.overallRating}
          message={getMessage('overallRating')}
          title="Rate your overall experience"
        />
        <OverallExperienceDetailsQuestion
          onChange={handleValueChangeWithValidation({
            setter,
            validatorProperty: 'overallDescription',
            previousValue: review,
          })}
          value={review?.overallDescription}
          tone={getTone('overallDescription')}
          message={getMessage('overallDescription')}
        />
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
